export const EXTERNAL_CUSTOMER_API_DOC = {
    title: 'External Customer API',
    version: '1.0.0',
    updatedAt: '2026-03-31',
    markdownTemplate: `# External Customer API

Tài liệu này mô tả bộ API để hệ thống bên thứ 3:

- đọc danh sách và chi tiết khách hàng của từng công ty
- đọc lịch sử hội thoại của khách hàng
- gửi ngược lại tóm tắt cuộc gọi sau khi tương tác với khách

Mọi request đều dùng \`x-api-key\` và chỉ truy cập được dữ liệu của đúng công ty sở hữu key đó.

## 1. Cách cấp API key

Trong CRM:

- Vào \`Quản lý -> Công ty -> Kết nối bên thứ 3\`
- Nhấn \`Tạo API key\`
- Lưu lại key ngay khi hệ thống hiển thị

Mỗi API key gắn với đúng \`1 công ty\`. Hệ thống bên thứ 3 dùng key nào thì chỉ xem được dữ liệu khách hàng và hội thoại của công ty tương ứng.

## 2. Authentication

Gửi API key qua header:

~~~http
x-api-key: <YOUR_API_KEY>
~~~

Nếu key không hợp lệ hoặc đã thu hồi, API trả về \`401\`.

## 3. Scope hiện tại

API key hiện có các scope:

- \`customers:read\`: đọc danh sách và chi tiết khách hàng
- \`conversations:read\`: đọc lịch sử hội thoại
- \`call_summaries:write\`: gửi tóm tắt cuộc gọi về CRM

## 4. Base path

~~~http
/api/v1/ext
~~~

Các endpoint hiện có:

~~~http
GET /api/v1/ext/customers
GET /api/v1/ext/customers/:contact_id
GET /api/v1/ext/customers/:contact_id/messages
POST /api/v1/ext/customers/:contact_id/call-summaries
~~~

## 5. API danh sách khách hàng

### Request

~~~http
GET /api/v1/ext/customers?page=1&limit=20&search=lan
x-api-key: <YOUR_API_KEY>
~~~

### Query params

- \`page\`: số trang, mặc định \`1\`
- \`limit\`: số bản ghi mỗi trang, mặc định \`20\`, tối đa \`100\`
- \`search\`: tìm theo tên hiển thị hoặc số điện thoại
- \`updated_from\`: lọc theo \`updated_time\` từ mốc timestamp milliseconds
- \`updated_to\`: lọc theo \`updated_time\` đến mốc timestamp milliseconds

### Response mẫu

~~~json
{
  "code": 200,
  "data": [
    {
      "contact_id": "contact_123",
      "user_id": "user_abc",
      "full_name": "Nguyễn Thị Lan",
      "display_name": "Lan Facebook",
      "avatar": "https://...",
      "phone_list": ["0901234567"],
      "email": "lan@example.com",
      "address": "HCM",
      "source_type": "direct",
      "channel_name": "Fanpage ABC",
      "assigned_sale": "65f1...",
      "sale_status": 1,
      "tags": [
        {
          "id": 10,
          "name": "Khách mới",
          "color": "#7ed321"
        }
      ],
      "snippet": "Khách hỏi về gói dịch vụ...",
      "last_interaction_at": 1710000000000,
      "created_time": 1709000000000,
      "updated_time": 1710000000000,
      "lead_score": 82,
      "ai_stage": "NEGOTIATION",
      "potential_level": "high",
      "potential_summary": "Tiềm năng cao - giai đoạn NEGOTIATION - Khách quan tâm báo giá",
      "extracted_info": {
        "notes": "Khách quan tâm báo giá"
      },
      "collected_info": {
        "full_name": "Nguyễn Thị Lan",
        "phone": "0901234567",
        "email": "lan@example.com",
        "address": "HCM"
      }
    }
  ],
  "paging": {
    "page": 1,
    "limit": 20,
    "total": 120
  }
}
~~~

## 6. API chi tiết khách hàng

### Request

~~~http
GET /api/v1/ext/customers/contact_123
x-api-key: <YOUR_API_KEY>
~~~

### Response mẫu

~~~json
{
  "code": 200,
  "data": {
    "contact_id": "contact_123",
    "user_id": "user_abc",
    "full_name": "Nguyễn Thị Lan",
    "display_name": "Lan Facebook",
    "avatar": "https://...",
    "phone_list": ["0901234567"],
    "email_list": ["lan@example.com"],
    "source_type": "direct",
    "channel_name": "Fanpage ABC",
    "snippet": "Khách hỏi về gói dịch vụ...",
    "media_id": "",
    "assigned_sale": "65f1...",
    "sale_status": 1,
    "tags": [
      {
        "id": 10,
        "name": "Khách mới",
        "color": "#7ed321"
      }
    ],
    "created_time": 1709000000000,
    "updated_time": 1710000000000,
    "last_interaction_at": 1710000000000,
    "lead_score": 82,
    "ai_stage": "NEGOTIATION",
    "potential_level": "high",
    "potential_summary": "Tiềm năng cao - giai đoạn NEGOTIATION - Khách quan tâm báo giá",
    "extracted_info": {
      "notes": "Khách quan tâm báo giá"
    },
    "memory_summary": "Khách đã hỏi về giá, timeline triển khai và muốn được tư vấn tiếp.",
    "semantic_memory": {
      "budget": "10-20 triệu",
      "interests": ["CRM", "tự động hóa chăm sóc"],
      "sentiment": "positive"
    },
    "memory_updated_at": "2026-03-19T08:15:00.000Z",
    "collected_info": {
      "full_name": "Nguyễn Thị Lan",
      "phone": "0901234567",
      "email": "lan@example.com",
      "address": "HCM",
      "full_name_other": "Lan Nguyen"
    }
  }
}
~~~

## 7. API lịch sử hội thoại của khách

API này trả về hội thoại theo \`contact_id\`. Dùng được cho các khách có nguồn như inbox, zalo, telegram, webchat, whatsapp, comment, callcenter.

Mỗi lần gọi API:

- hệ thống lấy batch tin nhắn mới nhất theo \`limit\`
- dữ liệu trong \`items\` luôn trả theo thứ tự cũ -> mới để bên thứ 3 render trực tiếp
- nếu còn dữ liệu cũ hơn, API trả \`paging.next_cursor\`

### Request lấy batch đầu tiên

~~~http
GET /api/v1/ext/customers/contact_123/messages?limit=20
x-api-key: <YOUR_API_KEY>
~~~

### Query params

- \`limit\`: số message muốn lấy, mặc định \`20\`, tối đa \`100\`
- \`before_timestamp\`: cursor timestamp để load các message cũ hơn
- \`before_key\`: cursor key để load tiếp khi có nhiều message cùng \`timestamp\`

### Response mẫu

~~~json
{
  "code": 200,
  "data": {
    "contact_id": "contact_123",
    "source_type": "whatsapp",
    "total": 135,
    "items": [
      {
        "message_id": "wamid.HBgL...",
        "timestamp": 1710000000000,
        "direction": "inbound",
        "item_type": "text",
        "content": "Bên mình còn gói nào cho spa không?",
        "attachments": [],
        "sender": {
          "id": "84901234567@c.us",
          "name": "Nguyễn Thị Lan"
        },
        "quote": null,
        "reaction": null,
        "created_time": 1710000000123,
        "updated_time": 1710000000456
      },
      {
        "message_id": "wamid.HBgM...",
        "timestamp": 1710000005000,
        "direction": "outbound",
        "item_type": "text",
        "content": "Dạ có chị, em gửi chị bảng giá trước nhé.",
        "attachments": [],
        "sender": {
          "id": "65f1...",
          "name": "CRM"
        },
        "quote": null,
        "reaction": null,
        "created_time": 1710000005234,
        "updated_time": 1710000005234
      }
    ],
    "paging": {
      "limit": 20,
      "returned": 20,
      "has_more": true,
      "next_cursor": {
        "before_timestamp": 1709999900000,
        "before_key": "wamid.HBgF..."
      }
    }
  }
}
~~~

## 8. Cách load more hội thoại

### Nguyên tắc

Lần đầu tiên gọi:

~~~http
GET /api/v1/ext/customers/contact_123/messages?limit=20
~~~

Nếu response trả:

~~~json
{
  "paging": {
    "has_more": true,
    "next_cursor": {
      "before_timestamp": 1709999900000,
      "before_key": "wamid.HBgF..."
    }
  }
}
~~~

Thì lần tiếp theo dùng đúng cursor đó:

~~~http
GET /api/v1/ext/customers/contact_123/messages?limit=20&before_timestamp=1709999900000&before_key=wamid.HBgF...
~~~

### Cách ghép dữ liệu ở client

- Request đầu tiên: render \`items\` như batch hiện tại
- Request load more: lấy batch cũ hơn và prepend lên đầu danh sách hiện tại
- Dừng load more khi \`paging.has_more = false\`

### Vì sao phải gửi cả \`before_timestamp\` và \`before_key\`

- \`before_timestamp\` giúp lấy các bản ghi cũ hơn
- \`before_key\` giúp phân biệt chính xác khi nhiều message có cùng \`timestamp\`
- Nếu chỉ dùng \`before_timestamp\`, có thể bị thiếu hoặc trùng message ở các mốc thời gian trùng nhau

## 9. Giải thích các trường của API hội thoại

### \`data\`

- \`contact_id\`: mã khách trong CRM
- \`source_type\`: nguồn hội thoại của khách, ví dụ \`direct\`, \`zalo\`, \`webchat\`, \`telegram\`, \`whatsapp\`, \`comment\`, \`callcenter\`
- \`total\`: tổng số message/call/comment hiện có của khách đó
- \`items\`: danh sách bản ghi hội thoại của batch hiện tại
- \`paging\`: thông tin phân trang cursor

### \`paging\`

- \`limit\`: kích thước batch đã yêu cầu
- \`returned\`: số item thực tế trong response hiện tại
- \`has_more\`: còn dữ liệu cũ hơn hay không
- \`next_cursor\`: cursor cho request kế tiếp
- \`next_cursor.before_timestamp\`: timestamp của item cũ nhất trong batch hiện tại
- \`next_cursor.before_key\`: khóa của item cũ nhất trong batch hiện tại

### \`items[]\`

- \`message_id\`: mã duy nhất của message/comment/call record
- \`timestamp\`: thời điểm message xảy ra, dạng milliseconds
- \`direction\`: chiều tin nhắn
- \`item_type\`: loại nội dung, ví dụ \`text\`, \`attachment\`, \`image\`, \`comment_reply\`, \`record\`
- \`content\`: nội dung text hoặc mô tả ngắn của record
- \`attachments\`: danh sách file đính kèm
- \`sender\`: thông tin người gửi
- \`created_time\`: thời điểm bản ghi được lưu vào DB CRM
- \`updated_time\`: thời điểm bản ghi được cập nhật lần cuối

### \`direction\`

- \`inbound\`: khách hàng gửi vào CRM
- \`outbound\`: CRM, sale hoặc bot gửi ra khách hàng

### \`sender\`

- \`sender.id\`: id người gửi theo dữ liệu nguồn
- \`sender.name\`: tên hiển thị của người gửi nếu hệ thống có dữ liệu

### \`attachments[]\`

- \`type\`: loại file, ví dụ \`image\`, \`file\`, \`audio\`
- \`url\`: URL file để bên thứ 3 tải hoặc preview
- \`name\`: tên file nếu có
- \`payload\`: dữ liệu gốc của attachment đang lưu trong CRM

### Các trường mở rộng theo từng \`source_type\`

#### Nguồn chat trực tiếp như \`direct\`, \`zalo\`, \`webchat\`, \`telegram\`, \`whatsapp\`

- \`quote\`: dữ liệu message được reply/quote nếu có
- \`reaction\`: dữ liệu reaction nếu có

#### Nguồn \`comment\`

- \`parent_id\`: id comment cha, dùng để nối thread reply
- \`is_hidden\`: trạng thái comment đang bị ẩn hay không

#### Nguồn \`callcenter\`

- \`status\`: trạng thái cuộc gọi
- \`duration\`: thời lượng cuộc gọi, đơn vị giây
- \`phone\`: số điện thoại của khách
- \`extnum\`: máy nhánh xử lý
- \`extnums\`: danh sách máy nhánh liên quan
- \`start_time\`: thời điểm bắt đầu cuộc gọi
- \`stop_time\`: thời điểm kết thúc cuộc gọi
- \`record_file_url\`: link file ghi âm nếu có
- \`call_type\`: loại cuộc gọi theo hệ thống callcenter

## 10. API ghi nhận tóm tắt cuộc gọi

API này cho phép hệ thống call center gửi ngược lại phần tóm tắt sau cuộc gọi để CRM:

- lưu lịch sử nhiều phiên gọi
- đồng bộ ngay vào bộ nhớ AI
- tự trích xuất thông tin khách từ text summary để cập nhật hồ sơ khách và AI memory
- ưu tiên dữ liệu từ \`structured_data\` nếu request có gửi kèm

### Request

~~~http
POST /api/v1/ext/customers/contact_123/call-summaries
x-api-key: <YOUR_API_KEY>
content-type: application/json
~~~

### Body mẫu

~~~json
{
  "external_call_id": "call_20260401_000123",
  "summary_text": "Khách xác nhận đang ở số nhà 123 đường ABC, muốn được gọi lại chốt lịch vào chiều thứ 6 và quan tâm gói niềng răng.",
  "structured_data": {
    "customer": {
      "name": "Nguyễn Thị Lan",
      "phone": "0901234567",
      "email": "lan@example.com",
      "address": "123 đường ABC, phường 5, quận 3, TP.HCM"
    },
    "interests": ["Niềng răng", "Tư vấn nha khoa tổng quát"],
    "budget": "10 triệu",
    "objections": ["Lo ngại chi phí phát sinh"],
    "appointment_date": "Chiều thứ 6 lúc 15h",
    "branch_location": "Công ty Quận 3",
    "notes": "Khách muốn được gọi lại xác nhận lịch",
    "topicsDiscussed": ["Địa chỉ khách", "Lịch hẹn", "Gói niềng răng"],
    "sentiment": "positive"
  },
  "called_at": 1775030280000,
  "agent_name": "Callbot A"
}
~~~

### Request fields

- \`external_call_id\`: id cuộc gọi từ hệ thống call center, dùng để chống gửi trùng
- \`summary_text\`: optional, nội dung tóm tắt text sau cuộc gọi
- \`structured_data\`: optional, khối dữ liệu cấu trúc nếu hệ thống call center đã có sẵn thông tin khách
- \`called_at\`: optional, timestamp milliseconds của thời điểm gọi
- \`agent_name\`: optional, tên agent hoặc callbot đã thực hiện cuộc gọi

### Quy tắc xử lý

- bắt buộc phải có ít nhất \`summary_text\` hoặc \`structured_data\`
- nếu gửi cả hai, CRM vẫn trích xuất thêm từ \`summary_text\` nhưng sẽ ưu tiên giá trị trong \`structured_data\` khi lưu vào AI memory và hồ sơ khách
- nếu \`structured_data\` chỉ gửi một phần, hệ thống vẫn tiếp tục đọc \`summary_text\` để điền các field còn thiếu
- \`external_call_id\` nên được gửi nếu hệ thống call center có id cuộc gọi ổn định
- nếu thiếu \`external_call_id\`, CRM tự tạo fingerprint từ payload để chống callback trùng ở mức cơ bản
- mỗi cuộc gọi được lưu thành một lịch sử riêng, không ghi đè phiên cũ
- CRM sẽ gọi nội bộ sang AI service để:
  - cập nhật \`memory_summary\`
  - cập nhật \`semantic_memory\`
  - trích xuất họ tên, số điện thoại, email, địa chỉ, lịch hẹn, nhu cầu nếu có trong text
  - merge với \`structured_data\` và ưu tiên giá trị từ \`structured_data\`

### Response mẫu

~~~json
{
  "code": 200,
  "data": {
    "company_id": 12,
    "contact_id": "contact_123",
    "user_id": "84901234567",
    "summary_text": "Khách xác nhận đang ở số nhà 123 đường ABC, muốn được gọi lại chốt lịch vào chiều thứ 6 và quan tâm gói niềng răng.",
    "agent_name": "Callbot A",
    "structured_data": {
      "customer": {
        "name": "Nguyễn Thị Lan",
        "phone": "0901234567",
        "email": "lan@example.com",
        "address": "123 đường ABC, phường 5, quận 3, TP.HCM"
      },
      "interests": ["Niềng răng", "Tư vấn nha khoa tổng quát"],
      "budget": "10 triệu",
      "objections": ["Lo ngại chi phí phát sinh"],
      "appointment_date": "Chiều thứ 6 lúc 15h",
      "branch_location": "Công ty Quận 3",
      "notes": "Khách muốn được gọi lại xác nhận lịch"
    },
    "source": "callcenter_external",
    "called_at": 1775030280000,
    "sync_status": "synced",
    "sync_error": "",
    "synced_at": 1775030281234,
    "created_time": 1775030281234,
    "updated_time": 1775030281234
  },
  "ai_memory": {
    "memory_summary": "Khách Nguyễn Thị Lan ở 123 đường ABC, phường 5, quận 3, TP.HCM, quan tâm niềng răng và muốn được gọi lại xác nhận lịch hẹn chiều thứ 6 lúc 15h tại công ty Quận 3.",
    "semantic_memory": {
      "customer": {
        "name": "Nguyễn Thị Lan",
        "phone": "0901234567",
        "email": "lan@example.com",
        "address": "123 đường ABC, phường 5, quận 3, TP.HCM"
      },
      "interests": ["Niềng răng", "Tư vấn nha khoa tổng quát"],
      "budget": "10 triệu",
      "objections": ["Lo ngại chi phí phát sinh"],
      "appointment_date": "Chiều thứ 6 lúc 15h",
      "branch_location": "Công ty Quận 3",
      "sentiment": "positive"
    },
    "lead_info": {
      "name": "Nguyễn Thị Lan",
      "phone": "0901234567",
      "email": "lan@example.com",
      "address": "123 đường ABC, phường 5, quận 3, TP.HCM",
      "product": "Niềng răng",
      "objections": ["Lo ngại chi phí phát sinh"],
      "notes": "Lịch hẹn: Chiều thứ 6 lúc 15h | Địa chỉ: 123 đường ABC, phường 5, quận 3, TP.HCM | Công ty: Công ty Quận 3 | Quan tâm: Niềng răng, Tư vấn nha khoa tổng quát | Ngân sách: 10 triệu | Lưu ý: Lo ngại chi phí phát sinh"
    },
    "imported": true
  }
}
~~~

### Giải thích response

- \`data\`: bản ghi lịch sử tóm tắt cuộc gọi đã được lưu trong CRM
- \`data.structured_data\`: giữ lại payload cấu trúc đã gửi sang để đối soát khi cần
- \`sync_status\`
  - \`synced\`: đã đồng bộ thành công sang AI memory
  - \`failed\`: đã lưu lịch sử nhưng AI sync lỗi
  - \`pending\`: trạng thái trung gian trong lúc backend đang xử lý
- \`sync_error\`: mô tả lỗi nếu AI sync thất bại
- \`ai_memory\`: dữ liệu bộ nhớ AI sau khi import thành công

## 11. Mã lỗi thường gặp

- \`200\`: thành công
- \`400\`: request không hợp lệ hoặc thiếu field bắt buộc
- \`401\`: thiếu API key, API key sai hoặc đã bị thu hồi
- \`403\`: API key đúng nhưng không có scope hoặc không có quyền truy xuất lead này
- \`404\`: không tìm thấy lead

## 12. Gợi ý tích hợp

- đồng bộ lead định kỳ bằng \`/customers\` kết hợp \`updated_from\`
- khi người dùng mở chi tiết lead ở hệ thống bên thứ 3, gọi \`/customers/:contact_id\`
- khi người dùng mở khung chat, gọi \`/customers/:contact_id/messages?limit=20\`
- Sau cuộc gọi, gọi \`/customers/:contact_id/call-summaries\` với \`summary_text\`, hoặc \`structured_data\`, hoặc cả hai
- khi người dùng bấm \`Load more\`, dùng \`paging.next_cursor\`
- nếu muốn tránh trùng dữ liệu do retry webhook, luôn gửi \`external_call_id\`
- khi nghi ngờ key bị lộ, thu hồi key cũ và tạo key mới trong CRM

## 13. cURL mẫu

### Lấy danh sách khách hàng

~~~bash
curl --request GET \\
  --url '{{BASE_URL}}/customers?page=1&limit=20' \\
  --header 'x-api-key: YOUR_API_KEY'
~~~

### Lấy chi tiết khách hàng

~~~bash
curl --request GET \\
  --url '{{BASE_URL}}/customers/contact_123' \\
  --header 'x-api-key: YOUR_API_KEY'
~~~

### Lấy lịch sử hội thoại

~~~bash
curl --request GET \\
  --url '{{BASE_URL}}/customers/contact_123/messages?limit=20' \\
  --header 'x-api-key: YOUR_API_KEY'
~~~

### Load more lịch sử hội thoại

~~~bash
curl --request GET \\
  --url '{{BASE_URL}}/customers/contact_123/messages?limit=20&before_timestamp=1709999900000&before_key=wamid.HBgF...' \\
  --header 'x-api-key: YOUR_API_KEY'
~~~

### Gửi tóm tắt cuộc gọi

~~~bash
curl --request POST \\
  --url '{{BASE_URL}}/customers/contact_123/call-summaries' \\
  --header 'x-api-key: YOUR_API_KEY' \\
  --header 'content-type: application/json' \\
  --data '{
    "external_call_id": "call_20260401_000123",
    "summary_text": "Khách xác nhận đang ở số nhà 123 đường ABC, muốn được gọi lại chốt lịch vào chiều thứ 6 và quan tâm gói niềng răng.",
    "structured_data": {
      "customer": {
        "name": "Nguyễn Thị Lan",
        "phone": "0901234567",
        "email": "lan@example.com",
        "address": "123 đường ABC, phường 5, quận 3, TP.HCM"
      },
      "interests": ["Niềng răng", "Tư vấn nha khoa tổng quát"],
      "budget": "10 triệu",
      "objections": ["Lo ngại chi phí phát sinh"],
      "appointment_date": "Chiều thứ 6 lúc 15h",
      "branch_location": "Công ty Quận 3",
      "notes": "Khách muốn được gọi lại xác nhận lịch",
      "topicsDiscussed": ["Địa chỉ khách", "Lịch hẹn", "Gói niềng răng"],
      "sentiment": "positive"
    },
    "called_at": 1775030280000,
    "agent_name": "Callbot A"
  }'
~~~`
};

export const buildExternalCustomerApiMarkdown = (baseUrl) => (
    EXTERNAL_CUSTOMER_API_DOC.markdownTemplate.split('{{BASE_URL}}').join(baseUrl)
);
